import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import { ScenarioSizingExt } from '@/classes/scenario/sizing/ScenarioSizingExt';
import { Specifications } from '@/services/ruleService';
import _log from '@/helpers/log';
import { ScenarioSizingResult } from '@/dto/scenario/sizing/ScenarioSizingResult';
import _rulez from '@/helpers/rulesHelper';
import { SizingProgress } from '@/dto/scenario/sizing/SizingProgress';
import { FieldStatus } from '@/classes/FieldStatus';
import { ScenarioPayloads } from '@/store/modules/scenario';
import { INestedRule } from '@/classes/rules/INestedRule';

const getDefaultFields = () => new ScenarioSizingExt();

type Dependencies = {
  http: {
    get: <Result>(url: string) => Promise<{ data: Result }>
    post: <Type>(url: string, payload: unknown) => Promise<{data: Type}>
  }
  assignSpecificationsToRules: (specifications: Specifications, rules: INestedRule<ScenarioExt>) => void
  errorHandler: (error: unknown) => void
}

type StartSizingResponse = {
  uuid: string
}

const hasResponseStatus = (value: unknown): value is { response: { status: number }} => {
  return value instanceof Object && 'response' in value;
};

export const buildSizingModule = ({ http, assignSpecificationsToRules, errorHandler }: Dependencies) => {
  function computePayload(scenario: ScenarioExt, fieldsStatuses: FieldStatus[]) {
    const clone = scenario.clone();
    _rulez.cleanScenario(clone, fieldsStatuses);
    const payload = clone.toDto();
    return {
      sizing: payload.sizing,
      battery: payload.battery,
      generator: payload.generator
    };
  }
  @Module({
    namespaced: true
  })
  class SizingModule extends VuexModule {

    private sizingMap: Map<string, ScenarioSizingResult> = new Map();
    private progressMap: Map<string, SizingProgress> = new Map();

    private isAlreadySizing: boolean = false;

    @Action
    public saveFields(payload: ScenarioSizingExt) {
      const currentScenario = this.getConfig;
      currentScenario.sizing = payload;
      this.context.dispatch(...ScenarioPayloads.SaveFields.buildPayload({ scenario: currentScenario }));
    }

    @Action
    public loadDefault(id: string) {
      return http.get<{ scenario: Specifications }>(`/scenario/${id}/sizing/default`)
        .then(({ data }) => data)
        .then(({ scenario }) => assignSpecificationsToRules(scenario, this.context.rootGetters['scenarioRules/rule']([])))
        .catch(errorHandler);
    }

    @Action
    public async startSizing(id: string) {
      this.context.dispatch('overlay/enable', {
        progress: 'sizing/getSizingProgress',
        cancel: 'sizing/cancelSizing'
      }, { root: true });
      this.context.dispatch('scenario/invalidAlgoResults', undefined, { root: true });
      this.context.commit('setIsAlreadySizing', false);
      const payload = computePayload(this.getConfig, this.getFieldStatuses);
      try {
        const { data } = await http.post<StartSizingResponse>(`/scenario/${id}/sizing/start`, payload);
        if (this.getConfig.sizing && data) {
          this.getConfig.sizing.uuid = data.uuid;
          await this.context.dispatch(...ScenarioPayloads.SaveFields.buildPayload({ scenario: this.getConfig }));
          await this.context.dispatch('scenario/updateScenario', this.getConfig, { root: true });
        }
        this.context.commit('setSizingMap', { id: this.scenarioId, data: null });
        this.context.dispatch('loadProgress', id);
      } catch (error) {
        this.context.dispatch('overlay/disable', { withCancel: false }, { root: true });
        if (hasResponseStatus(error) && error.response.status === 409) {
          this.context.commit('setIsAlreadySizing', true);
        }
        errorHandler(error);
      }
    }

    @Action
    public async loadProgress(id: string) {
      try {
        const { data } = await http.get<SizingProgress>(`/scenario/${id}/sizing/progress`);
        if (data) {
          const { progress, size } = data;
          this.context.commit('setProgress', { id: this.scenarioId, data });
          if (progress !== null && size !== null && progress < size) {
            setTimeout(() => {
              this.context.dispatch('loadProgress', id);
            }, 500);
          } else if (progress === size) {
            this.context.dispatch('overlay/disable', { withCancel: false }, { root: true });
            if (this.getConfig.sizing && this.getConfig.sizing.uuid) {
              this.getConfig.sizing.uuid = null;
              await this.context.dispatch(...ScenarioPayloads.SaveFields.buildPayload({ scenario: this.getConfig }));
              await this.context.dispatch('scenario/updateScenario', this.getConfig, { root: true });
              this.context.dispatch('loadSizingResult', id);
            }
          }
        } else {
          this.context.commit('setProgress', { id: this.scenarioId, data: null });
        }
      } catch {
        this.context.commit('setProgress', { id: this.scenarioId, data: null });
        this.context.dispatch('overlay/disable', { withCancel: false }, { root: true });
      }
    }

    @Action
    public loadSizingResult(id: string) {
      return http.get(`/scenario/${id}/sizing/result`)
        .then((res: any) => {
          const result = (res.data && new ScenarioSizingResult(res.data)) || null;
          this.context.commit('setSizingMap', { id: this.scenarioId, data: result });
        }).catch(_log.logError);
    }

    @Action
    public async cancelSizing(): Promise<void> {
      try {
        await http.get(`/scenario/${this.getConfig.uuid}/sizing/cancel`);
        if (this.getConfig.sizing) {
          this.getConfig.sizing.uuid = null;
          await this.context.dispatch(...ScenarioPayloads.SaveFields.buildPayload({ scenario: this.getConfig }));
          await this.context.dispatch('scenario/updateScenario', this.getConfig, { root: true });
        }
      } catch (error) {
        errorHandler(error);
      }
    }

    @Action
    public invalidSizingById(scenarioId: string = this.scenarioId) {
      const payload = { id: scenarioId, data: null };
      this.context.commit('setSizingMap', payload);
    }

    // Mutations --------------------------------------------------------

    @Mutation
    private setIsAlreadySizing(bool: boolean) {
      this.isAlreadySizing = bool;
    }

    @Mutation
    private setSizingMap(payload: { id: string; data: ScenarioSizingResult }) {
      this.sizingMap.set(payload.id, payload.data);
      this.sizingMap = new Map(this.sizingMap);
    }

    @Mutation
    private setProgress(payload: { id: string; data: SizingProgress }) {
      if (payload.data) {
        this.progressMap.set(payload.id, payload.data);
      } else {
        this.progressMap.delete(payload.id);
      }
      this.progressMap = new Map(this.progressMap);
    }

    // Getters --------------------------------------------------------

    get getConfig(): ScenarioExt {
      return new ScenarioExt(this.context.rootGetters['scenario/getFields']);
    }

    get scenarioId(): string {
      return this.getConfig.uuid || '';
    }

    get getFieldStatuses(): FieldStatus[] {
      return this.context.rootGetters['scenario/getFieldStatuses'];
    }

    get getFields(): ScenarioSizingExt {
      return this.getConfig.sizing ? new ScenarioSizingExt(this.getConfig.sizing) : getDefaultFields();
    }

    get getSizingProgress(): SizingProgress | null {
      return this.progressMap.get(this.scenarioId) || null;
    }

    get getSizingResult(): ScenarioSizingResult | null {
      return this.sizingMap.get(this.scenarioId) || null;
    }

    get getIsAlreadySizing(): boolean {
      return this.isAlreadySizing;
    }
  }

  return SizingModule;
};
